import React, { useState } from 'react';
import { useInvoice } from "../../pages/InvoiceApp";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Alert, AlertTitle, AlertDescription } from "../ui/alert";
import { format } from "date-fns";
import { CalendarIcon, AlertCircle, PlusIcon, MinusIcon, ChevronUpIcon, ChevronDownIcon, Trash2 as Trash2Icon } from "lucide-react";
import { cn } from "../../lib/utils";
import { useTheme } from '../ui/theme-provider'; // Import useTheme

// Import the SimpleCalendar component for consistency with MonthlyInvoice
import { SimpleCalendar } from "../ui/simple-calendar";

const MonthlyInvoiceForm = () => {
  // Get state from context
  const {
    entries, 
    setEntries,
    rateType,
    setRateType,
    rateValue,
    setRateValue,
    total,
    setTotal,
    setSummary,
    handleSaveInvoice, // Destructure handleSaveInvoice from context
    setShowSummary // New: control summary UI
  } = useInvoice();
  
  // Local state
  const [validationError, setValidationError] = useState('');
  const [calendarOpen, setCalendarOpen] = useState(null);
  const { theme } = useTheme(); // Get theme
  
  // Clear validation error when rate value or type changes
  // But not on every render or when validationError itself changes
  React.useEffect(() => {
    if (validationError) {
      // Only clear the validation error when the user actually changes the inputs
      // not when the component re-renders due to other state changes
      setValidationError('');
    }
    
    // Add cleanup function to ensure state is reset when component unmounts
    return () => {
      // This helps avoid state conflicts when switching between views
    };
  }, [rateType, rateValue]); // Remove validationError from dependencies
  
  // Handlers
  const handleAddEntry = () => {
    const newEntries = entries.map(entry => ({
      ...entry,
      isExpanded: false
    }));
    
    setEntries([...newEntries, { 
      date: null, 
      hours: '', 
      description: '',
      patients: [{ count: '', rate: '' }], 
      isExpanded: true 
    }]);
  };
  
  const handleRemoveEntry = (indexToDelete) => {
    setEntries(entries.filter((_, index) => index !== indexToDelete));
  };
  
  const handleToggleEntry = (index) => {
    const newEntries = entries.map((entry, i) => 
      i === index ? {...entry, isExpanded: !entry.isExpanded} : entry
    );
    setEntries(newEntries);
  };
  
  const handleDateChange = (entryIndex, date) => {
    const newEntries = [...entries];
    newEntries[entryIndex] = {
      ...newEntries[entryIndex],
      date
    };
    setEntries(newEntries);
  };
  
  const handleEntryChange = (dateIndex, field, value) => {
    const newEntries = [...entries];
    newEntries[dateIndex] = {
      ...newEntries[dateIndex],
      [field]: value
    };
    setEntries(newEntries);
  };
  
  const handleAddPatient = (entryIndex) => {
    const newEntries = [...entries];
    if (!newEntries[entryIndex].patients) {
      newEntries[entryIndex].patients = [];
    }
    
    newEntries[entryIndex].patients.push({ count: '', rate: '', description: '' });
    setEntries(newEntries);
  };
  
  const handleRemovePatient = (entryIndex, patientIndex) => {
    const newEntries = [...entries];
    if (newEntries[entryIndex].patients.length > 1) {
      newEntries[entryIndex].patients = newEntries[entryIndex].patients.filter(
        (_, idx) => idx !== patientIndex
      );
      setEntries(newEntries);
    }
  };
  
  const handlePatientChange = (entryIndex, patientIndex, field, value) => {
    const newEntries = [...entries];
    newEntries[entryIndex].patients[patientIndex] = {
      ...newEntries[entryIndex].patients[patientIndex],
      [field]: value
    };
    setEntries(newEntries);
  };
  
  const validateAndCalculateTotal = () => {
    // Basic validation - skip rate value check for per_patient since it uses individual patient rates
    if (rateType !== 'per_patient' && (!rateValue || rateValue.trim() === '')) {
      setValidationError(`Please enter a ${rateType === 'percentage' ? 'percentage' : 'rate'} value`);
      return;
    }
    
    // Validate entries have dates
    const entriesWithoutDates = entries.filter(entry => !entry.date);
    if (entriesWithoutDates.length > 0) {
      setValidationError('Please add dates to all entries');
      return;
    }
    
    // Only clear validation error after successful validation
    setValidationError('');
    
    // Calculate totals based on rate type
    let totalAmount = 0;
    let totalPatients = 0;
    let totalHours = 0;
    const calculatedSummary = [];
    
    // Group entries by date
    const validEntries = entries.filter(entry => entry.date);
    const groupedEntries = validEntries.reduce((acc, entry) => {
      const dateKey = format(entry.date, "yyyy-MM-dd");
      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(entry);
      return acc;
    }, {});

    // Process each date group
    Object.entries(groupedEntries).forEach(([dateKey, dateEntries]) => {
      const formattedDate = format(dateEntries[0].date, "dd MMM yyyy");
      let dateTotal = 0;
      let datePatients = 0;
      let dateHours = 0;
      let dateLines = [];

      dateEntries.forEach(entry => {
        if (rateType === 'hourly') {
          const hours = parseFloat(entry.hours) || 0;
          const hourlyRate = parseFloat(rateValue) || 0;
          const entryAmount = hours * hourlyRate;
          dateHours += hours;
          dateTotal += entryAmount;
          
          // Use provided description if available, or just show the hours
          const descText = entry.description ? 
            `${hours} hours × £${hourlyRate.toFixed(2)} (${entry.description})` :
            `${hours} hours × £${hourlyRate.toFixed(2)}`;
            
          dateLines.push(descText);
        } 
        else if (rateType === 'percentage') {
          console.log('🔥🔥🔥 NEW PERCENTAGE LOGIC IN MonthlyInvoiceForm.jsx 🔥🔥🔥');
          if (entry.patients && entry.patients.length > 0) {
            entry.patients.forEach(patient => {
              const count = parseInt(patient.count) || 0;
              const rate = parseFloat(patient.rate) || 0;
              const patientCollections = count * rate; // Collections without percentage
              
              datePatients += count;
              dateTotal += patientCollections; // Accumulate collections (no percentage yet)
              
              // Show patient details WITHOUT percentage
              const pluralText = count !== 1 ? 's' : '';
              const descText = patient.description ? 
                `${count} patient${pluralText} @ £${rate.toFixed(2)} (${patient.description})` : 
                `${count} patient${pluralText} @ £${rate.toFixed(2)}`;
                
              console.log('🔥 Building line:', descText);
              dateLines.push(descText);
            });
          }
        }
        else if (rateType === 'per_patient') {
          if (entry.patients && entry.patients.length > 0) {
            entry.patients.forEach(patient => {
              const count = parseInt(patient.count) || 0;
              const rate = parseFloat(patient.rate) || 0;
              const patientAmount = count * rate;
              
              datePatients += count;
              dateTotal += patientAmount;
              
              // Use provided description if available
              const descText = patient.description ? 
                `${count} × £${rate.toFixed(2)} (${patient.description})` : 
                `${count} × £${rate.toFixed(2)}`;
                
              dateLines.push(descText);
            });
          }
        }
        else if (rateType === 'fixed') {
          const fixedAmount = parseFloat(rateValue) || 0;
          dateTotal += fixedAmount;
          dateHours += parseFloat(entry.hours) || 0;
          
          // Use provided description if available
          const descText = entry.description ? 
            `Fixed rate: £${fixedAmount.toFixed(2)} (${entry.description})` : 
            `Fixed rate: £${fixedAmount.toFixed(2)}`;
            
          dateLines.push(descText);
        }
      });

      // Create summary item with properly formatted value
      if (rateType === 'percentage') {
        // For percentage, show collections (no percentage applied yet)
        calculatedSummary.push({
          label: formattedDate,
          value: `${dateLines.join(' + ')} = £${dateTotal.toFixed(2)}`
        });
      } else {
        // For other types, keep existing format
        calculatedSummary.push({
          label: formattedDate,
          value: `${dateLines.join(' | ')} = £${dateTotal.toFixed(2)}`
        });
      }

      // Update totals
      totalHours += dateHours;
      totalPatients += datePatients;
      totalAmount += dateTotal;
    });
    
    // For percentage invoices, add summary rows and apply percentage
    if (rateType === 'percentage') {
      const percentageRate = parseFloat(rateValue) || 0;
      const totalCollections = totalAmount; // At this point, totalAmount = collections
      const percentageAmount = totalCollections * (percentageRate / 100);
      
      // Add subtotal row
      calculatedSummary.push({
        label: 'Subtotal (patient fees)',
        value: `= £${totalCollections.toFixed(2)}`,
        isSubtotal: true
      });
      
      // Add percentage calculation row
      calculatedSummary.push({
        label: `Your ${percentageRate}% commission`,
        value: `= £${percentageAmount.toFixed(2)}`,
        isPercentage: true
      });
      
      // Add total row
      calculatedSummary.push({
        label: 'Invoice Total',
        value: `= £${percentageAmount.toFixed(2)}`,
        isTotal: true
      });
      
      // Update totalAmount to be the percentage amount
      totalAmount = percentageAmount;
      
      console.log('🔥🔥🔥 PERCENTAGE SUMMARY COMPLETE:', calculatedSummary);
    }
    
    if (rateType === 'percentage' || rateType === 'per_patient') {
      setTotal({
        amount: totalAmount,
        patients: totalPatients
      });
    } else {
      setTotal({
        amount: totalAmount,
        hours: totalHours
      });
    }
    
    // Update the summary with consistent formatting
    setSummary(calculatedSummary);
  };
  
  // Enhanced date picker component with proper styling
  const EnhancedDatePicker = ({ selected, onSelect, entryIndex }) => {
    // Calendar component itself should ideally handle theme internally or via props
    // We style the wrapper
    return (
      // Wrapper: Theme-aware background
      <div className={cn("p-3 rounded-lg", theme === 'light' ? 'bg-white' : 'bg-[#2A2A3E]')}>
        <div className="calendar-wrapper">
          <SimpleCalendar
            value={selected}
            onChange={(date) => {
              onSelect(date);
              setCalendarOpen(null); // Close calendar after date selection
            }}
            // Calendar: Rely on its internal theme handling or pass theme prop if available
            className="border-none" 
          />
        </div>
      </div>
    );
  };
  
  const handleDoneClick = () => {
    // Perform validation - skip rate value check for per_patient since it uses individual patient rates
    if (rateType !== 'per_patient' && (!rateValue || rateValue.trim() === '')) {
      setValidationError(`Please enter a ${rateType === 'percentage' ? 'percentage' : 'rate'} value`);
      return;
    }
    const entriesWithoutDates = entries.filter(entry => !entry.date);
    if (entriesWithoutDates.length > 0) {
      setValidationError('Please add dates to all entries');
      return;
    }
    setValidationError('');
    // Calculate totals and summary (but do not show summary UI here)
    let totalAmount = 0;
    let totalPatients = 0;
    let totalHours = 0;
    const calculatedSummary = [];
    const validEntries = entries.filter(entry => entry.date);
    const groupedEntries = validEntries.reduce((acc, entry) => {
      const dateKey = format(entry.date, "yyyy-MM-dd");
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(entry);
      return acc;
    }, {});
    Object.entries(groupedEntries).forEach(([dateKey, dateEntries]) => {
      const formattedDate = format(dateEntries[0].date, "dd MMM yyyy");
      let dateTotal = 0;
      let datePatients = 0;
      let dateHours = 0;
      let dateLines = [];
      dateEntries.forEach(entry => {
        if (rateType === 'hourly') {
          const hours = parseFloat(entry.hours) || 0;
          const hourlyRate = parseFloat(rateValue) || 0;
          const entryAmount = hours * hourlyRate;
          dateHours += hours;
          dateTotal += entryAmount;
          const descText = entry.description ? `${hours} hours × £${hourlyRate.toFixed(2)} (${entry.description})` : `${hours} hours × £${hourlyRate.toFixed(2)}`;
          dateLines.push(descText);
        } else if (rateType === 'percentage') {
          console.log('🔥🔥🔥 NEW PERCENTAGE LOGIC IN handleDoneClick() 🔥🔥🔥');
          if (entry.patients && entry.patients.length > 0) {
            entry.patients.forEach(patient => {
              const count = parseInt(patient.count) || 0;
              const rate = parseFloat(patient.rate) || 0;
              const patientCollections = count * rate; // Collections without percentage
              datePatients += count;
              dateTotal += patientCollections; // Accumulate collections (no percentage yet)
              // Show patient details WITHOUT percentage
              const pluralText = count !== 1 ? 's' : '';
              const descText = patient.description ? `${count} patient${pluralText} @ £${rate.toFixed(2)} (${patient.description})` : `${count} patient${pluralText} @ £${rate.toFixed(2)}`;
              console.log('🔥 handleDoneClick line:', descText);
              dateLines.push(descText);
            });
          }
        } else if (rateType === 'per_patient') {
          if (entry.patients && entry.patients.length > 0) {
            entry.patients.forEach(patient => {
              const count = parseInt(patient.count) || 0;
              const rate = parseFloat(patient.rate) || 0;
              const patientAmount = count * rate;
              datePatients += count;
              dateTotal += patientAmount;
              const descText = patient.description ? `${count} × £${rate.toFixed(2)} (${patient.description})` : `${count} × £${rate.toFixed(2)}`;
              dateLines.push(descText);
            });
          }
        } else if (rateType === 'fixed') {
          const fixedAmount = parseFloat(rateValue) || 0;
          dateTotal += fixedAmount;
          dateHours += parseFloat(entry.hours) || 0;
          const descText = entry.description ? `Fixed rate: £${fixedAmount.toFixed(2)} (${entry.description})` : `Fixed rate: £${fixedAmount.toFixed(2)}`;
          dateLines.push(descText);
        }
      });
      calculatedSummary.push({
        label: formattedDate,
        value: `${dateLines.join(' | ')} = £${dateTotal.toFixed(2)}`
      });
      totalHours += dateHours;
      totalPatients += datePatients;
      totalAmount += dateTotal;
    });
    
    // For percentage invoices, add special summary rows
    if (rateType === 'percentage') {
      console.log('🔥🔥🔥 Adding special rows for percentage invoice in handleDoneClick');
      const percentageRate = parseFloat(rateValue) || 0;
      const subtotal = totalAmount; // totalAmount is now collections (without percentage)
      const percentageAmount = subtotal * (percentageRate / 100);
      const invoiceTotal = percentageAmount;
      
      // Add separator and special rows
      calculatedSummary.push({
        label: 'Total Charged to Patients',
        value: `£${subtotal.toFixed(2)}`,
        isSubtotal: true
      });
      calculatedSummary.push({
        label: `Commission (${percentageRate}%)`,
        value: `£${percentageAmount.toFixed(2)}`,
        isPercentage: true
      });
      calculatedSummary.push({
        label: 'Invoice Total',
        value: `£${invoiceTotal.toFixed(2)}`,
        isTotal: true
      });
      
      // Update totalAmount to be the actual invoice total for setTotal
      totalAmount = invoiceTotal;
      console.log('🔥 Subtotal:', subtotal, 'Percentage:', percentageRate, 'Invoice Total:', invoiceTotal);
    }
    
    if (rateType === 'percentage' || rateType === 'per_patient') {
      setTotal({ amount: totalAmount, patients: totalPatients });
    } else {
      setTotal({ amount: totalAmount, hours: totalHours });
    }
    setSummary(calculatedSummary);
    setShowSummary(false); // Hide summary UI after Done
  };
  
  return (
    <>
      {/* Nuclear CSS override for light mode inputs */}
      {theme === 'light' && (
        <style dangerouslySetInnerHTML={{
          __html: `
            /* Force white backgrounds for ALL inputs in light mode */
            input[type="text"],
            input[type="number"],
            input[type="email"],
            input:not([type]) {
              background-color: white !important;
              background: white !important;
            }
            
            /* Target by class names too */
            .bg-white {
              background-color: white !important;
              background: white !important;
            }
            
            /* Override any hover/focus states */
            input[type="text"]:hover,
            input[type="number"]:hover,
            input[type="email"]:hover,
            input:not([type]):hover {
              background-color: white !important;
              background: white !important;
            }
            
            input[type="text"]:focus,
            input[type="number"]:focus,
            input[type="email"]:focus,
            input:not([type]):focus {
              background-color: white !important;
              background: white !important;
            }
          `
        }} />
      )}

      {/* Invoice Details Card */}
      <Card className={cn(
        "shadow-lg",
        theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#2A2A3E] border-purple-500/20'
      )}>
        <CardContent className="p-3">
          {/* Title: Theme-aware */}
          <h2 className={cn(
            "text-base font-medium mb-2",
            theme === 'light' ? 'text-black' : 'text-white'
          )}>Invoice Details</h2>
          <div className="flex items-center gap-2">
            <div className="flex-grow">
              {/* Label: Theme-aware */}
              <Label htmlFor="invoiceType" className={cn(
                "text-xs mb-1 block",
                theme === 'light' ? 'text-gray-700' : 'text-gray-300'
              )}>Invoice Type</Label>
              <Select value={rateType} onValueChange={setRateType}>
                {/* Trigger: Theme-aware */}
                <SelectTrigger id="invoiceType" className={cn(
                  "text-xs h-9",
                  theme === 'light' 
                    ? 'bg-white border-gray-300 text-black' 
                    : 'bg-[#1F1F2E] border-purple-500/30 text-white'
                )}>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                {/* Content: Theme-aware */}
                <SelectContent className={cn(
                  theme === 'light' ? 'bg-white border-gray-300' : 'bg-[#2A2A3E] border-purple-500/30'
                )}>
                  {/* Items: Theme-aware */}
                  <SelectItem value="percentage" className={cn(theme === 'light' ? 'text-black hover:bg-gray-100' : 'text-white hover:bg-purple-500/20')}>Percentage</SelectItem>
                  <SelectItem value="hourly" className={cn(theme === 'light' ? 'text-black hover:bg-gray-100' : 'text-white hover:bg-purple-500/20')}>Hourly</SelectItem>
                  <SelectItem value="fixed" className={cn(theme === 'light' ? 'text-black hover:bg-gray-100' : 'text-white hover:bg-purple-500/20')}>Fixed</SelectItem>
                  <SelectItem value="per_patient" className={cn(theme === 'light' ? 'text-black hover:bg-gray-100' : 'text-white hover:bg-purple-500/20')}>Per Patient</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {rateType && rateType !== 'per_patient' && (
              <div className="w-24">
                {/* Label: Theme-aware */}
                <Label htmlFor="rateValue" className={cn(
                  "text-xs mb-1 block",
                  theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                )}>
                  {rateType === 'percentage' ? '%' : '£'}
                </Label>
                {/* Rate value input */}
                <Input
                  id="rateValue"
                  type="number"
                  value={rateValue}
                  onChange={(e) => setRateValue(e.target.value)}
                  className={cn(
                    "text-xs h-9",
                    theme === 'light' 
                      ? 'bg-white border-gray-300 text-black' 
                      : 'bg-[#1F1F2E] border-purple-500/30 text-white'
                  )}
                  style={theme === 'light' ? { backgroundColor: 'white !important', borderColor: '#d1d5db !important' } : {}}
                  placeholder="Value"
                  step={rateType === 'percentage' ? '1' : '0.01'}
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Validation Error: Theme-aware */}
      {validationError && (
        <Alert variant="destructive" className={cn(
          theme === 'light' 
            ? 'bg-red-50 border-red-200 text-red-700' 
            : 'bg-red-900/20 border-red-500/50 text-red-200'
        )}>
          <AlertCircle className="h-3 w-3 sm:h-4 sm:w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription className="text-xs sm:text-sm">{validationError}</AlertDescription>
        </Alert>
      )}
      
      {/* Entry Card: Theme-aware */}
      <Card className={cn(
        "shadow-lg",
        theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#2A2A3E] border-purple-500/20'
      )}>
        <CardContent className="p-3 sm:p-4">
          <div className="flex justify-between items-center mb-3 sm:mb-4">
            {/* Title: Theme-aware */}
            <h2 className={cn(
              "text-base sm:text-lg font-medium",
              theme === 'light' ? 'text-black' : 'text-white'
            )}>Entry Details</h2>
          </div>
          
          {/* Entry list */}
          {entries.map((entry, entryIndex) => (
            <div
              key={`entry-${entryIndex}`}
              // Entry Item Container: Theme-aware
              className={cn(
                "p-2 sm:p-3 rounded-lg border mb-3",
                theme === 'light' 
                  ? 'bg-gray-50 border-gray-200' 
                  : 'bg-[#1F1F2E] border-purple-500/10'
              )}
            >
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center">
                  {/* Toggle Button: Theme-aware */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleEntry(entryIndex)}
                    className="p-1 h-6 w-6 sm:h-7 sm:w-7 mr-1"
                  >
                    {entry.isExpanded ? (
                      <ChevronUpIcon className={cn("h-4 w-4 sm:h-5 sm:w-5", theme === 'light' ? 'text-purple-600' : 'text-purple-400')} />
                    ) : (
                      <ChevronDownIcon className={cn("h-4 w-4 sm:h-5 sm:w-5", theme === 'light' ? 'text-gray-500' : 'text-gray-400')} />
                    )}
                  </Button>
                  {/* Entry Title/Date: Theme-aware */}
                  <h3 className={cn(
                    "text-xs sm:text-sm font-medium",
                    theme === 'light' ? 'text-black' : 'text-white'
                  )}>
                    {entry.date ? format(entry.date, 'dd MMM yyyy') : 'New Entry'}
                  </h3>
                </div>
                <div>
                  {/* Remove Entry Button: Theme-aware */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRemoveEntry(entryIndex)}
                    disabled={entries.length <= 1}
                    className={cn(
                      "h-8 w-8 p-0 flex items-center justify-center",
                      theme === 'light' 
                        ? 'bg-red-100 text-red-600 hover:bg-red-200 border-red-300' 
                        : 'bg-red-900/10 text-red-400 hover:text-red-300 hover:bg-red-900/20 border border-red-500/20'
                    )}
                  >
                    <Trash2Icon className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {entry.isExpanded && (
                <div className="pl-5 sm:pl-7 mt-2 sm:mt-3 space-y-2 sm:space-y-4">
                  {/* First row - always show date */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      {/* Label: Theme-aware */}
                      <Label className={cn("text-xs mb-1 block", theme === 'light' ? 'text-gray-700' : 'text-gray-300')}>Date</Label>
                      <Popover open={calendarOpen === entryIndex} onOpenChange={(isOpen) => setCalendarOpen(isOpen ? entryIndex : null)}>
                        <PopoverTrigger asChild>
                          {/* Popover Trigger Button: Theme-aware */}
                          <div
                            role="button"
                            tabIndex={0}
                            className={cn(
                              "flex h-9 w-full rounded-md border px-3 py-2 text-sm shadow-sm cursor-pointer",
                              theme === 'light' 
                                ? 'bg-white border-gray-300 text-black hover:bg-gray-50' 
                                : 'bg-[#1E1E2D] border-gray-700 text-white hover:bg-[#2A2A3E]',
                              !entry.date && (theme === 'light' ? "text-gray-500" : "text-gray-400")
                            )}
                            onClick={() => setCalendarOpen(entryIndex)}
                            onKeyDown={(e) => e.key === "Enter" && setCalendarOpen(entryIndex)}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 opacity-70" />
                            {entry.date ? format(entry.date, "dd/MM/yyyy") : "Select date"}
                          </div>
                        </PopoverTrigger>
                        {/* Popover Content: Theme-aware */}
                        <PopoverContent className={cn(
                          "w-auto p-0",
                          theme === 'light' ? 'bg-white border-gray-300' : 'bg-[#2A2A3E] border-purple-500/30'
                        )}>
                          <EnhancedDatePicker
                            selected={entry.date}
                            onSelect={(date) => handleDateChange(entryIndex, date)}
                            entryIndex={entryIndex}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    
                    {/* Hours field */}
                    {(rateType === 'hourly' || rateType === 'fixed' || entry.hours) && (
                      <div>
                        {/* Label: Theme-aware */}
                        <Label htmlFor={`hours-${entryIndex}`} className={cn("text-xs mb-1 block", theme === 'light' ? 'text-gray-700' : 'text-gray-300')}>Hours</Label>
                        {/* Input: Theme-aware */}
                        <Input
                          id={`hours-${entryIndex}`}
                          type="number"
                          value={entry.hours || ''}
                          onChange={(e) => handleEntryChange(entryIndex, 'hours', e.target.value)}
                          className={cn(
                            "text-sm",
                            theme === 'light' ? 'bg-white border-gray-300 text-black' : 'bg-[#2A2A3E] border-gray-700 text-white'
                          )}
                          style={theme === 'light' ? { backgroundColor: 'white !important', borderColor: '#d1d5db !important' } : {}}
                          step="0.5"
                        />
                      </div>
                    )}
                  </div>
                  
                  {/* Patient fields section */}
                  {(rateType === 'percentage' || rateType === 'per_patient') && (
                    <div>
                      <div className="flex justify-between items-center mb-3">
                        {/* Label: Theme-aware */}
                        <Label className={cn("text-xs", theme === 'light' ? 'text-gray-700' : 'text-gray-300')}>Patient Records</Label>
                        {/* Add Patient Button: Theme-aware */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAddPatient(entryIndex)}
                          className={cn(
                            "h-7",
                            theme === 'light' 
                              ? 'border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100' 
                              : 'border border-purple-500/30 bg-purple-900/20 text-purple-300 hover:bg-purple-500/20 hover:text-purple-200 active:bg-purple-900/20'
                          )}
                        >
                          <PlusIcon className="h-4 w-4" />
                          <span className="text-xs ml-1">Add Patient Type</span>
                        </Button>
                      </div>
                      
                      {/* Patient entries */}
                      {entry.patients && entry.patients.map((patient, patientIndex) => (
                        <div 
                          key={`patient-${entryIndex}-${patientIndex}`}
                          className="grid grid-cols-12 gap-2 mb-2 items-end"
                        >
                          <div className="col-span-2">
                            {/* Label: Theme-aware */}
                            <Label htmlFor={`count-${entryIndex}-${patientIndex}`} className={cn("text-xs mb-1 block", theme === 'light' ? 'text-gray-500' : 'text-gray-400')}>Count</Label>
                            {/* Input: Theme-aware */}
                            <Input
                              id={`count-${entryIndex}-${patientIndex}`}
                              type="number"
                              value={patient.count || ''}
                              onChange={(e) => handlePatientChange(entryIndex, patientIndex, 'count', e.target.value)}
                              className={cn(
                                "text-sm",
                                theme === 'light' ? 'bg-white border-gray-300 text-black' : 'bg-[#2A2A3E] border-gray-700 text-white'
                              )}
                              style={theme === 'light' ? { backgroundColor: 'white !important', borderColor: '#d1d5db !important' } : {}}
                            />
                          </div>
                          
                          <div className="col-span-2">
                            {/* Label: Theme-aware */}
                            <Label htmlFor={`rate-${entryIndex}-${patientIndex}`} className={cn("text-xs mb-1 block", theme === 'light' ? 'text-gray-500' : 'text-gray-400')}>Rate</Label>
                            {/* Input: Theme-aware */}
                            <Input
                              id={`rate-${entryIndex}-${patientIndex}`}
                              type="number"
                              value={patient.rate || ''}
                              onChange={(e) => handlePatientChange(entryIndex, patientIndex, 'rate', e.target.value)}
                              className={cn(
                                "text-sm",
                                theme === 'light' ? 'bg-white border-gray-300 text-black' : 'bg-[#2A2A3E] border-gray-700 text-white'
                              )}
                              style={theme === 'light' ? { backgroundColor: 'white !important', borderColor: '#d1d5db !important' } : {}}
                            />
                          </div>
                          
                          <div className="col-span-7">
                            {/* Label: Theme-aware */}
                            <Label htmlFor={`description-${entryIndex}-${patientIndex}`} className={cn("text-xs mb-1 block", theme === 'light' ? 'text-gray-500' : 'text-gray-400')}>Description</Label>
                            {/* Input: Theme-aware */}
                            <Input
                              id={`description-${entryIndex}-${patientIndex}`}
                              value={patient.description || ''}
                              onChange={(e) => handlePatientChange(entryIndex, patientIndex, 'description', e.target.value)}
                              className={cn(
                                "text-sm",
                                theme === 'light' ? 'bg-white border-gray-300 text-black' : 'bg-[#2A2A3E] border-gray-700 text-white'
                              )}
                              style={theme === 'light' ? { backgroundColor: 'white !important', borderColor: '#d1d5db !important' } : {}}
                            />
                          </div>
                          
                          <div className="col-span-1">
                            {/* Remove Patient Button: Theme-aware */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemovePatient(entryIndex, patientIndex)}
                              disabled={entry.patients.length <= 1}
                              className={cn(
                                "h-8 w-8 p-0 flex items-center justify-center",
                                theme === 'light' 
                                  ? 'text-red-500 hover:text-red-700 hover:bg-red-100' 
                                  : 'text-red-400 hover:text-red-300 hover:bg-red-900/20'
                              )}
                            >
                              <MinusIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Description field for hourly/fixed */}
                  {(rateType === 'hourly' || rateType === 'fixed') && (
                    <div>
                      {/* Label: Theme-aware */}
                      <Label htmlFor={`description-${entryIndex}`} className={cn("text-xs mb-1 block", theme === 'light' ? 'text-gray-700' : 'text-gray-300')}>Description</Label>
                      {/* Input: Theme-aware */}
                      <Input
                        id={`description-${entryIndex}`}
                        value={entry.description || ''}
                        onChange={(e) => handleEntryChange(entryIndex, 'description', e.target.value)}
                        className={cn(
                          "text-sm",
                          theme === 'light' ? 'bg-white border-gray-300 text-black' : 'bg-[#2A2A3E] border-gray-700 text-white'
                        )}
                        style={theme === 'light' ? { backgroundColor: 'white !important', borderColor: '#d1d5db !important' } : {}}
                        placeholder="Description of work"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Add Another Date Button: Theme-aware */}
          <div className="mt-3 sm:mt-4 flex justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddEntry}
              className={cn(
                "text-xs sm:text-sm",
                theme === 'light' 
                  ? 'border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100' 
                  : 'border-purple-500/30 bg-purple-900/20 text-purple-300 hover:bg-purple-500/20'
              )}
            >
              <PlusIcon className="h-3 w-3 mr-1" /> Add Another Date Worked
            </Button>
          </div>

          {/* Calculate Total Button: Consistent purple */}
          <div className="mt-3 sm:mt-4 flex justify-end">
            <Button
              onClick={handleDoneClick}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 sm:px-6 text-xs sm:text-sm font-medium"
            >
              Done
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default React.memo(MonthlyInvoiceForm);
